<template>
  <div v-hotkey.stop="keymap" class="smart-player">
    <div class="smart-player__display" @wheel.prevent="zoomVideo">
      <template v-if="canEditZones">
        <template v-for="currentZoneId in 4">
          <!-- Зоны только для показа - все кроме выбранной для редактирования. -->
          <ZonesPainter
            v-show="!showError && !isLoading && !showHelpForZones && showZonePainter && showAllZones"
            :key="currentZoneId"
            :initial-points="zones[currentZoneId-1].points"
            :zone-color="zoneOptions[currentZoneId-1].color"
            :zone-height="visibleVideoHeight"
            :zone-width="visibleVideoWidth"
          />
        </template>
        <!-- Одна зона только для редактирования. -->
        <ZonesPainter
          v-show="!showError && !isLoading && !showHelpForZones && showZonePainter"
          :initial-points="zones[selectedZoneId].points"
          :zone-color="zoneOptions[selectedZoneId].color"
          :zone-height="visibleVideoHeight"
          :zone-width="visibleVideoWidth"
          @save-points="zones[selectedZoneId].points = $event"
        />
      </template>
      <template v-if="canEditLines">
        <LinePainter
          v-show="!showError && !isLoading && showLinePainter"
          :initial-points="lines[0].points[0]"
          :initial-enter-point="lines[0].points[1]"
          :line-height="visibleVideoHeight"
          :line-width="visibleVideoWidth"
          @save-points="lines[0].points[0] = $event"
          @save-enter-point="lines[0].points[1] = $event"
        />
      </template>
      <video
        v-show="!showError && !isLoading && !showHelpForZones"
        ref="videoPlayer"
        v-bind="useSimpleInterface && !expectationArchiveMode ? {'controls': 'controls'} : {}"
        :style="{ transform: `matrix(${sourceMatrix.join(',')}) translate3d(0, 0, 0)` }"
        autoplay
        class="smart-player__video"
        muted
        @canplay="onCanPlay()"
        @dblclick="switchFullscreen()"
        @error="onError"
        @mousedown="onMouseDownOnVideo()"
        @mouseleave="hideControls()"
        @mousemove="showThenHideControls()"
        @timeupdate="onTimeUpdate()"
      />
      <div v-if="isLoading" class="smart-player__loading" @mouseleave="hideControls()" @mousemove="showThenHideControls();">
        <SpinnerLoading size="l" />
      </div>
      <template v-else-if="showError">
        <div v-if="showFatalError" class="smart-player__error">
          <div class="smart-player__error-title">
            {{ $t('errorOccurred') }}
          </div>
          <CamsButton class="smart-player__error-btn" type="button" @click="reloadPage()">
            {{ $t('refreshPage') }}
          </CamsButton>
        </div>
        <div v-else-if="showArchiveAvailableError" class="smart-player__error">
          <div class="smart-player__error-title">
            {{ $t('archiveFragmentUnavailable') }}
          </div>
        </div>
      </template>
      <div v-else-if="showHelpForZones" class="smart-player__help">
        <div class="smart-player__help-title">
          {{ $t('analyticsZonesHints') }}
        </div>
        <div class="smart-player__help-text">
          <p>{{ $t('addPointToZone') }}</p>
          <br>
          <p>{{ $t('removePointFromZone') }}</p>
          <br>
          <p>{{ $t('removeAllPointsFromZone') }}</p>
          <br>
          <p>{{ $t('movePoint') }}</p>
          <br>
          <p>
            {{ $t('editZonesIndividually') }}<br>
            {{ $t('selectZoneToEdit') }}
          </p>
          <br>
          <p>
            {{ $t('maxPointsInZone') }}
          </p>
        </div>
      </div>
    </div>

    <SmartPlayerTimeline
      v-if="!useSimpleInterface && canArchiveMode"
      ref="smartPlayerTimeline"
      :available-archive-from="availableArchiveFrom"
      :available-archive-to="availableArchiveTo"
      :block-live-mode="blockLiveMode"
      :download-mode="downloadMode"
      :get-line-fragments="getLineFragments"
      :get-component-with-content="getComponentWithScreenshot"
      :initial-download-from="downloadFrom"
      :initial-download-to="downloadTo"
      :initial-follow-time-shift="true"
      :initial-size="timelineSize"
      :max-unix-download-delta="maxUnixDownloadDelta"
      :min-unix-download-delta="minUnixDownloadDelta"
      :time-shift="timeShift"
      class="smart-player__timeline"
      @download-borders-change="changeDownloadBorders"
      @time-shift-change="updateCurrentTimeByTimeShift"
      @timeline-size-change="timelineSize = $event"
    />

    <div
      v-if="expectationArchiveMode"
      v-show="!useSimpleInterface || showControls"
      :class="{'smart-player__controls_in-display': useSimpleInterface}"
      class="smart-player__controls smart-player-controls"
      @mouseenter="showThenHideControls()"
      @mouseleave="hideControls()"
      @mousemove="showThenHideControls()"
    >
      <div v-if="canArchiveMode && !useSimpleInterface" v-show="downloadMode" class="mode-settings">
        <SmartInputDate
          v-model="downloadFrom"
          :config-flat-pickr="{...configFlatPickr, minDate: availableArchiveFrom, maxDate: availableArchiveTo}"
          :one-row="true"
          :caption="$t('from')"
          class="mode-settings__input-date"
          :locale="locale"
        />
        <SmartInputDate
          v-model="downloadTo"
          :config-flat-pickr="{...configFlatPickr, minDate: availableArchiveFrom, maxDate: availableArchiveTo}"
          :one-row="true"
          :caption="$t('to')"
          class="mode-settings__input-date"
          :locale="locale"
        />
        <SmartSwitch v-model="needTimelapse" :caption="$t('acceleratedVideo')" />
        <CamsButton class="mode-settings__action" priority="primary" type="button" @click="downloadVideo()">
          {{ $t('download') }}
        </CamsButton>
        <CamsButton class="mode-settings__action" type="button" @click="downloadMode = false">
          {{ $t('cancel') }}
        </CamsButton>
      </div>

      <div v-if="canEditZones && !useSimpleInterface && showZonesOptionsFlag" v-show="showZonePainter" class="mode-settings">
        <SmartSelect
          v-model="selectedZoneId"
          :options="zoneOptions"
          :caption="$t('analyticsZone')"
          label-field="title"
          :placeholder="$t('availableZones')"
          sort-field="id"
          value-field="id"
          width="fix"
        />
        <SmartSwitch
          v-model="showAllZones"
          :one-row="false"
          :caption="$t('showAll')"
        />
        <SmartInputText
          v-model="zones[selectedZoneId].sensitivity"
          :caption="$t('sensitivity')"
          class="mode-settings__input-text"
          input-type="number"
        />
        <CamsButton
          class="mode-settings__action"
          priority="secondary"
          :title="$t('deleteAllPointsInZone')"
          type="button"
          @click="zones[selectedZoneId].points = []"
        >
          {{ $t('clean') }}
        </CamsButton>
        <CamsButton
          class="mode-settings__action"
          priority="primary"
          :title="$t('saveAllZones')"
          type="button"
          @click="saveAllZones()"
        >
          {{ $t('save') }}
        </CamsButton>
        <CamsButton
          class="mode-settings__action"
          :title="$t('showHelp')"
          type="button"
          @click="showHelpForZones = !showHelpForZones"
        >
          ?
        </CamsButton>
      </div>
      <CamsButton
        v-show="showLinePainter"
        class="mode-settings__action"
        priority="primary"
        :title="$t('saveAllZones')"
        type="button"
        @click="saveAllLines()"
      >
        {{ $t('save') }}
      </CamsButton>
      <div v-if="canEditLines && !useSimpleInterface" v-show="showZonePainter" class="mode-settings">
        <CamsButton
          class="mode-settings__action"
          priority="secondary"
          :title="$t('deleteAllPointsInZone')"
          type="button"
          @click="zones[selectedZoneId].points = []"
        >
          {{ $t('clean') }}
        </CamsButton>
        <CamsButton
          class="mode-settings__action"
          priority="primary"
          :title="$t('saveAllZones')"
          type="button"
          @click="saveAllZones()"
        >
          {{ $t('save') }}
        </CamsButton>
      </div>
      <div v-show="ptzToolbarFlag && hasPtz" class="ptz-controls">
        <button class="ptz-button ptz-center" @click="handleCentralizePTZ()" />
        <button class="ptz-button ptz-up" @mousedown="handlePTZStart('Up')" @mouseup="handlePTZStop()"  />
        <button class="ptz-button ptz-down" @mousedown="handlePTZStart('Down')" @mouseup="handlePTZStop()"  />
        <button class="ptz-button ptz-left" @mousedown="handlePTZStart('Left')" @mouseup="handlePTZStop()"  />
        <button class="ptz-button ptz-right" @mousedown="handlePTZStart('Right')" @mouseup="handlePTZStop()"  />
      </div>
      <div class="smart-player-controls__main">
        <div v-if="canArchiveMode && !useSimpleInterface" class="smart-player-controls__group smart-player-controls__group_start">
          <div class="timeline-plus-minus">
            <CamsButton
              :disabled="timelineSize >= 8"
              class="player-button"
              icon-type="only"
              :title="$t('zoomOutArchiveScale')"
              type="button"
              @click="timelineSize++"
            >
              <svg :class="{'disabled': timelineSize >= 8}" class="player-icon">
                <use xlink:href="./img/icons.svg#icon-circle-minus" />
              </svg>
            </CamsButton>
            <CamsButton
              :disabled="timelineSize <= 0"
              class="player-button"
              icon-type="only"
              :title="$t('zoomInArchiveScale')"
              type="button"
              @click="timelineSize--"
            >
              <svg :class="{'disabled': timelineSize <= 0}" class="player-icon">
                <use xlink:href="./img/icons.svg#icon-circle-plus" />
              </svg>
            </CamsButton>
          </div>
          <CamsButton
            class="player-button"
            icon-type="only"
            :title="$t('goToCurrentMoment')"
            type="button"
            @click="showTimeShift()"
          >
            <svg class="player-icon">
              <use xlink:href="./img/icons.svg#icon-focus" />
            </svg>
          </CamsButton>
          <div class="datepicker" :title="$t('archiveCalendar')">
            <CamsButton
              icon-type="only"
              size="s"
              type="button"
              class="player-button"
              @click="$refs.archiveCalendar.open()"
            >
              <svg class="player-icon">
                <use xlink:href="./img/icons.svg#icon-calendar" />
              </svg>
            </CamsButton>
            <SmartInputDate
              ref="archiveCalendar"
              v-model="timeShift"
              :config-flat-pickr="{dateFormat: 'd.m H:i', allowInput: false, minDate: availableArchiveFrom, maxDate: availableArchiveTo, static: true}"
              :one-row="true"
              size="s"
              :locale="locale"
              @input="shiftCurrentTimeViaCalendar"
            />
          </div>
          <CamsButton
            v-if="!blockLiveMode"
            class="player-button"
            icon-type="only"
            :title="$t('goToLiveStream')"
            type="button"
            @click="!archiveMode ? (lowLatencyMode = !lowLatencyMode) : goToLive()"
          >
            <svg
              :class="archiveMode ? 'player-icon' : 'player-icon_live'"
              style="height: 14px;width: 37px;"
            >
              <use xlink:href="./img/icons.svg#icon-dot" />
            </svg>
          </CamsButton>
        </div>

        <div class="smart-player-controls__group smart-player-controls__group_center-or-start">
          <CamsButton
            v-if="canArchiveMode && !useSimpleInterface"
            class="player-button"
            icon-type="only"
            :title="$t('rewindBack15s')"
            type="button"
            @click="shiftCurrentTimeBySeconds(-15)"
          >
            <svg class="player-icon">
              <use xlink:href="./img/icons.svg#icon-go-back" />
            </svg>
          </CamsButton>
          <CamsButton
            v-show="paused"
            class="player-button"
            icon-type="only"
            :title="$t('launch')"
            type="button"
            @click="play()"
          >
            <svg class="player-icon">
              <use xlink:href="./img/icons.svg#icon-play" />
            </svg>
          </CamsButton>
          <CamsButton
            v-show="!paused"
            class="player-button"
            icon-type="only"
            :title="$t('pause')"
            type="button"
            @click="pause()"
          >
            <svg class="player-icon">
              <use xlink:href="./img/icons.svg#icon-pause" />
            </svg>
          </CamsButton>
          <CamsButton
            v-if="canArchiveMode && !useSimpleInterface"
            :disabled="!archiveMode"
            class="player-button"
            icon-type="only"
            :title="$t('fastForward15s')"
            type="button"
            @click="shiftCurrentTimeBySeconds(+15)"
          >
            <svg :class="{'disabled': !archiveMode}" class="player-icon">
              <use xlink:href="./img/icons.svg#icon-go-forward" />
            </svg>
          </CamsButton>
        </div>

        <div class="smart-player-controls__group smart-player-controls__group_end">
          <CamsButton
            v-if="hasPtz"
            class="player-button"
            icon-type="only"
            :title="$t('ptzControl')"
            @click="showPtzTooBar()"
          >
            <svg class="player-icon">
              <use xlink:href="./img/icons.svg#icon-ptz" />
            </svg>
          </CamsButton>
          <CamsButton
            class="player-button"
            icon-type="only"
            :title="$t('takeScreenshot')"
            type="button"
            @click="takeScreenshot()"
          >
            <svg :class="{'disabled': showError || isLoading}" class="player-icon">
              <use xlink:href="./img/icons.svg#icon-take-screenshot" />
            </svg>
          </CamsButton>
          <CamsButton
            v-if="canEditZones && !useSimpleInterface"
            class="player-button"
            icon-type="only"
            :title="$t('videoAnalysisZones')"
            @click="switchZonePainter()"
          >
            <svg :class="{'disabled': showError || isLoading, 'player-icon_mode-active': this.showZonePainter}" class="player-icon">
              <use xlink:href="./img/icons.svg#icon-zone-painter" />
            </svg>
          </CamsButton>
          <CamsButton
            v-if="canEditLines && !useSimpleInterface"
            class="player-button"
            icon-type="only"
            :title="$t('videoAnalysisZones')"
            @click="switchLinePainter()"
          >
            <svg :class="{'disabled': showError || isLoading, 'player-icon_mode-active': this.showZonePainter}" class="player-icon">
              <use xlink:href="./img/icons.svg#icon-zone-painter" />
            </svg>
          </CamsButton>
          <CamsButton
            v-if="canArchiveMode && !useSimpleInterface"
            v-click-outside="() => {showSpeedMenu = false}"
            class="player-button player-button_with-popup"
            icon-type="only"
            :title="$t('playbackSpeed')"
          >
            <span
              :class="{'disabled': !archiveMode}"
              class="player-icon player-icon_width-auto player-icon_text-center"
              @click="archiveMode && (showSpeedMenu = !showSpeedMenu)"
            >
              x<span v-text="playbackRate" />
            </span>
            <div v-show="showSpeedMenu" class="smart-player__popup smart-player__speed-select">
              <div
                :class="{'smart-player__popup-option_selected': playbackRate === 0.5}"
                class="smart-player__popup-option"
                @click="setPlaybackRate(0.5)"
              >
                x0.5
              </div>
              <div
                :class="{'smart-player__popup-option_selected': playbackRate === 1.0}"
                class="smart-player__popup-option"
                @click="setPlaybackRate(1.0)"
              >
                x1
              </div>
              <div
                :class="{'smart-player__popup-option_selected': playbackRate === 2.0}"
                class="smart-player__popup-option"
                @click="setPlaybackRate(2.0)"
              >
                x2
              </div>
              <div
                :class="{'smart-player__popup-option_selected': playbackRate === 4.0}"
                class="smart-player__popup-option"
                @click="setPlaybackRate(4.0)"
              >
                x4
              </div>
              <div
                :class="{'smart-player__popup-option_selected': playbackRate === 8.0}"
                class="smart-player__popup-option"
                @click="setPlaybackRate(8.0)"
              >
                x8
              </div>
            </div>
          </CamsButton>
          <CamsButton
            v-if="canArchiveMode && !useSimpleInterface"
            class="player-button"
            icon-type="only"
            :title="$t('downloadArchive')"
            type="button"
            @click="downloadMode = !downloadMode"
          >
            <svg :class="{'player-icon_mode-active': downloadMode}" class="player-icon">
              <use xlink:href="./img/icons.svg#icon-download" />
            </svg>
          </CamsButton>
          <CamsButton class="player-button volume-btn" icon-type="only" :title="$t('volume')" type="button">
            <svg v-show="muted || volume <= 0" :class="{'disabled': !hasSound}" class="player-icon" @click="hasSound && (muted = !muted)">
              <use xlink:href="./img/icons.svg#icon-muted-speaker" />
            </svg>
            <svg v-if="hasSound" v-show="!muted && volume > 0 && volume <= 0.50" class="player-icon" @click="muted = !muted">
              <use xlink:href="./img/icons.svg#icon-speaker" />
            </svg>
            <svg v-if="hasSound" v-show="!muted && volume > 0.50" class="player-icon" @click="muted = !muted">
              <use xlink:href="./img/icons.svg#icon-full-speaker" />
            </svg>
            <div v-if="hasSound" class="volume-range" @click="muted = false">
              <input
                v-model="volume"
                class="volume-range__input"
                max="1"
                min="0"
                step="0.1"
                type="range"
              >
            </div>
          </CamsButton>
          <CamsButton
            v-click-outside="() => {showSettingsMenu = false}"
            class="player-button player-button_with-popup"
            icon-type="only"
            :title="$t('settings')"
            type="button"
          >
            <svg class="player-icon" @click="showSettingsMenu = !showSettingsMenu">
              <use xlink:href="./img/icons.svg#icon-settings" />
            </svg>
            <div v-show="showSettingsMenu" class="smart-player__popup smart-player__settings smart-player-settings">
              <div class="smart-player-settings__streams">
                <div class="smart-player-settings__title">
                  {{ $t('streams') }}
                </div>
                <div
                  v-for="currentStreamNumber in streamCount"
                  :key="currentStreamNumber"
                  :class="{'smart-player__popup-option_selected': currentStreamNumber === streamNumber}"
                  class="smart-player__popup-option"
                  @click="setStreamNumber(currentStreamNumber)"
                >
                  {{ currentStreamNumber }}
                </div>
              </div>
              <div v-show="!useSimpleInterface" class="smart-player-settings__low-latency">
                <p>{{ $t('noDelayStream') }}</p>
                <SmartSwitch v-model="lowLatencyMode" />
              </div>
            </div>
          </CamsButton>
          <CamsButton
            :class="{'disabled': showZonePainter || showLinePainter}"
            class="player-button"
            icon-type="only"
            :title="$t('fullscreen')"
            type="button"
            @click="switchFullscreen()"
          >
            <svg class="player-icon">
              <use xlink:href="./img/icons.svg#icon-fullscreen" />
            </svg>
          </CamsButton>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import {
  AnalyticLine,
  AnalyticLinesConfig,
  AnalyticZone,
  AnalyticZonesConfig,
  playerBusMixin,
  PlayerHLS,
  PlayerMSE,
  PlayerMSEManul,
  ZONES_OPTIONS
} from "./mixins.js";
import ZonesPainter from "./ZonesPainter.vue";
import LinePainter from "./LinePainter.vue";
import SmartPlayerTimeline from "./SmartPlayerTimeline.vue";
import {DOWNLOADS_TYPES} from "../../lib/consts.js";
import {downloadBase64Content, exitFullscreen, requestFullscreenByElement} from "../../lib/helpers.js";

/**
 * Компонент плеера для проигрывания видео с камеры.
 *
 * # Режимы работы
 *
 * Плеер поддерживает несколько режимов работы, которые зависят от прав пользователя на камеру.
 * Разница между режимами включает в себя отображение доступных и сокрытие недоступных функций.
 *
 * 1. Просмотр только прямой трансляции: ограниченное количество элементов управления видео.
 *  1.1. Вариант просмотра в одном экране со внешними элементами управления.
 *  1.2. Вариант просмотра в мультиэкране (возможно на maps и для встраивания) со встроенными элементами управления в видео.
 * 2. Просмотр прямой трансляции и архива с возможностью скачивания: все элементы управления внешние.
 *
 * ## Установка начальных режимов работы
 *
 * Наличие фрагментов доступного архива позволяет
 * отрисовать временную шкалу - плеер получает возможность переключаться в режим работы просмотра архива.
 *
 * Одновременное наличие начального временного сдвига (`initialTimeShift`) и фрагментов доступного архива (`availableArchiveFragments`)
 * устанавливает режим работы плеера в архивный режим работы.
 * Параметры установленные для работы плеера в режиме низкой задержки игнорируются.
 *
 * Справедливо и наоборот - отсутствие фрагментов архива, позволяет работать плееру только в режиме прямой трансляции.
 * Учитываются начальные параметры для работы в режиме низкой задержки.
 *
 * # Библиотеки
 *
 * В зависимости от настроенных режимов работы плеера выбирается конкретная библиотека:
 *
 * - hls.js https://github.com/video-dev/hls.js
 * - от flussonic https://github.com/flussonic/mse-player
 *
 * Для прямой трансляции допускается использование MSE или HLS (по выбору), для архива только HLS.
 * Для воспроизведения потока создается объект плеера из местных абстракций. Они завязаны на реализации библиотек выше
 * и пробрасывают все внешние вызовы внутрь них. Для управления воспроизведением иногда плеер использует вызовы местных абстракций
 * (т.к. есть вендорные зависимости), а в тех случаях когда реализация стандартная, используются браузерные методы на элементе video.
 * Часть свойств элемента video для управления воспроизведением проброшена в компонент (см. data) и любое их изменение требует
 * проброса новых значений в свойства элемента video.
 *
 * # Замечания по video
 *
 * Элемент video работает с существующими фрагментами видео. Внутренний индикатор прогресса воспроизведения в video имеет на начальной позиции
 * всегда 0 и считается в секундах (см. `htmlMediaElement.currentTime`). Если поставить меньше 0 - самостоятельно вернется в 0,
 * если поставить больше чем длина видео - самостоятельно встанет в конец воспроизведения.
 *
 * На флюсонике видео может иметь (часто) пропуски, но для плеера в браузере это все равно будет цельный фрагмент,
 * просто в процессе воспроизведения в конце одного участка видео картинка сразу поменяется, при этом положение currentTime
 * будет продолжено без всяких смещений.
 *
 * Исходя из этого необходимо:
 *
 * 1. получать из сервера актуальные данные по существующим видео фрагментам за пределами компонента и передавать их в параметрах
 * 2. обрабатывать время указанное в доступных фрагментах и на основе этого делать смещение в шкале времени, отрисовке и прочих вычислениях
 * 3. при ручном сдвиге времени для плеера нужно высчитывать время которое он поймет - без смещения, от начала всего видео.
 *
 * > Из этого вытекает требование чтобы на внешней стороне всегда поставлялись корректные данные (которые можно запросить).
 * Договариваемся, что все фрагменты упорядочены (как в случае с `TimeRanges`) и начало первого из фрагментов = 0,
 * а начало каждого последующего будет рассчитываться исходя из начального значения первого фрагмента.
 *
 * # Замечания по глобальным обработчикам
 *
 * Чтобы увеличивать кадр в видео и перемещать видимый участок необходимо работать с мышью и для этого создаются глобальные обработчики
 * событий `mousemove` и `mouseup`, и удаляются после использования.
 * Аналогичный механизм используется и на временной шкале, детали описания см. там.
 *
 * # Замечания по отслеживанию одиночных кликов по видео
 *
 * Кликнув по видео можно остановить и продолжить воспроизведение видео. Увеличив кадр и зажав ЛКМ можно переместить видимый участок кадра.
 * Чтобы окончательно отделить процесс перемещения кадра видео от желания поставить его на паузу, есть флаг для отслеживания одиночного клика
 * {@see singleClickOnVideo}, который снимается через заданное количество мс после нажатия на ЛКМ по видео.
 *
 * Ранее было так что перемещение кадра {@see moveVideo} исключало событие клика ЛКМ для pause/play,
 * но полностью разделять эти два процесса оказалось очень не дружелюбно к пользователю.
 * Особенно в случаях когда пользователь визуально даже не чувствует что он двигал мышью,
 * поэтому лучшим решением было фиксировать время нажатия на ЛКМ.
 *
 * ## ToDo
 *
 * - если на хлс включить звук то при переходе к мсе он мьютится, а если на мсе включить звук и перейти на хлс - то он будет работать.
 */
export default {
  components: {
    ZonesPainter,
    SmartPlayerTimeline,
    LinePainter
  },
  mixins: [playerBusMixin],
  props: {
    /**
     * Строка с протоколом WebSocket для вставки в URL, по которому будет получено видео.
     *
     * @values ws, wss
     */
    wsProtocol: {
      type: String,
      required: true,
      default: "ws",
    },
    /**
     * Строка с протоколом HTTP для вставки в URL, по которому будет получено видео.
     *
     * @values http, https
     */
    httpProtocol: {
      type: String,
      required: true,
      default: "http",
    },
    /**
     * Домен сервера с которого можно получить поток с камеры.
     */
    domain: {
      type: String,
      required: true,
    },
    /**
     * Номер камеры с которой будет воспроизводится трансляция.
     */
    cameraNumber: {
      type: String,
      required: true,
    },
    /**
     * Токен для доступа к прямой трансляции.
     * Могут быть указаны оба токена, но хотя бы один должен быть.
     */
    liveToken: {
      type: String,
      default: null,
    },
    /**
     * Токен для доступа к архивной трансляции.
     * Могут быть указаны оба токена, но хотя бы один должен быть.
     */
    archiveToken: {
      type: String,
      default: null,
    },
    /**
     * Начальное значение номера потока для воспроизведения.
     */
    initialStreamNumber: {
      type: Number,
      default: 1,
    },
    /**
     * Количество доступных потоков.
     */
    streamCount: {
      type: Number,
      required: true,
    },
    /**
     * Начальное состояние флага для включения плеера режиме низкой задержки - MSE.
     * Актуально для запроса прямых трансляций, для архива логичнее использовать HLS.
     */
    initialLowLatencyMode: {
      type: Boolean,
      default: true,
    },
    /**
     * Массив из пар дат для рисования на временной шкале отрезков линий для обозначения доступного архива.
     * Для архива необходимо заранее запросить доступные фрагменты видео.
     * @see TimeRanges
     */
    availableArchiveFragments: {
      type: Array,
      default: () => ([]),
    },
    /**
     * Функция для работы механизма рисования линий на временной шкале. Пробрасывается напрямую в {@see SmartPlayerTimeline} см. детали там.
     */
    getLineFragments: {
      type: Function,
      default: null,
    },
    /**
     * Начало для воспроизведения архивного видео.
     */
    initialTimeShift: {
      type: Date,
      default: null,
    },
    /**
     * Флаг для включения управления плеером через горячие клавиши.
     */
    hotKeysEnabled: {
      type: Boolean,
      default: true,
    },
    /**
     * Флаг для принудительной блокировки функций перехода к прямой трансляции.
     *
     * Такой режим полезен если администратором регулируется доступ к прямой трансляции или к архиву.
     * При этом допускается регулярное пополнение данных по доступному архиву.
     */
    forceBlockLiveMode: {
      type: Boolean,
      default: false,
    },
    /**
     * Флаг, определяющий возможность настройки громкости.
     */
    hasSound: {
      type: Boolean,
      default: true,
    },
    /**
     * Флаг, определяющий возможность настройки PTZ.
     */
    hasPtz: {
      type: Boolean,
      default: false,
    },
    /**
     * Флаг, определяющий возможность настройки громкости.
     */
    vendorName: {
      type: String,
      default: null,
    },
    /**
     * Флаг, отображения меню для редактирования зон.
     */
    showZonesOptionsFlag: {
      type: Boolean,
      default: true,
    },
    /**
     * Режим ожидания архива.
     *
     * Если клиент смотрит архив по работающей камере, у которой есть прямая трансляция (т.е. архив постоянно пополняется свежими фрагментами),
     * тогда в плеер постоянно поступают (в {@see availableArchiveFragments}) свежие данные по доступному архиву,
     * чтобы иметь возможность показать свежее архивное видео.
     * Это обычный режим работы плеера и параметр по умолчанию = true.
     *
     * Однако бывают ситуации, когда надо показать архив строго в тех рамках которые определены во фрагментах доступного архива.
     * Полезно при просмотре коротких фрагментов видео, например на событиях. Тогда параметр нужно выключить,
     * а в интерфейсе имеет смысл отобразить стандартный интерфейс с прогрессом.
     */
    expectationArchiveMode: {
      type: Boolean,
      default: true,
    },
    /**
     * Флаг для скрытия внешнего (по отношению к видео) интерфейса и отображение элементов управления внутри участка видео.
     */
    useSimpleInterface: {
      type: Boolean,
      default: false,
    },
    /**
     * Набор настроек и прочей информации по зонам аналитики.
     */
    analyticZonesConfig: {
      type: AnalyticZonesConfig,
      default: null,
    },
    analyticLinesConfig: {
      type: AnalyticLinesConfig,
      default: null,
    },
    /**
     * Минимальная задержка в секундах, которая должна пройти, прежде чем плеер попытается самостоятельно восстановится после сбоя.
     */
    minDelayForRecovery: {
      type: Number,
      default: 0.5,
    },
    /**
     * Максимальная задержка в секундах, которая должна пройти, прежде чем плеер попытается самостоятельно восстановится после сбоя.
     */
    maxDelayForRecovery: {
      type: Number,
      default: 7,
    },
    /**
     * Максимальное количество сбоев перед отправкой уведомлений о них в родительский компонент.
     */
    maxDisasters: {
      type: Number,
      default: 5,
    },
    /**
     * Минимальное количество секунд для скачивания. Определяет границы скачивания на шкале и в календаре. По умолчанию 20 сек.
     */
    minUnixDownloadDelta: {
      type: Number,
      default: 20,
    },
    /**
     * Максимальное количество секунд для скачивания. Определяет границы скачивания на шкале и в календаре. По умолчанию 8 часов.
     */
    maxUnixDownloadDelta: {
      type: Number,
      default: 28800,
    },
    /**
     * Максимальная длительность архива (в секундах), которая запрашивается с сервера флюсоника во время воспроизведения.
     * Если воспроизведение архива позволяет использовать эту длительность, тогда используется она,
     * в противном случае длительность рассчитывается исходя из доступного архива.
     */
    maxArchiveDuration: {
      type: Number,
      default: 1800, // 30 мин.
    },
    /**
     * Начальное положение флага для определения возможности подключения видео трансляции по технологии low-latency HLS.
     * При воспроизведении через HLS плеер попытается подключится к флюсонику сначала по URL включающий в себя флаг для работы LLHLS,
     * а если это не сработает, то все дальнейшие попытки будут вести к работе через обычный HLS.
     *
     * todo поддержка llhls экспериментальная и поведение опции и логики работы плеера с этой технологией может меняться
     */
    possibleLowLatencyHls: {
      type: Boolean,
      default: false
    },
    /**
     * Режим работы автосдвига при проигрывании архива.
     * Режим предполагает следующее: когда прогресс достигает недоступного участка архива,
     * то воспроизведение нужно продолжить со следующего доступного участка. Это актуально когда пользователь смотрит одну камеру.
     *
     * В выключенном режиме воспроизведение должно остановится и отобразится сообщение о недоступности архива, на запрошенном времени.
     * Это полезно когда доступ к архиву запрашивается по нескольким камерам, и на некоторых архив недоступен.
     * В таком сценарии нелогично чтобы часть камер показывала запрошенный архив, а другая часть сдвигалась на доступный архив.
     */
    autoShiftMode: {
      type: Boolean,
      default: true,
    },
    /**
     * Функции для работы с вращением камеры по PTZ
     */
    onPTZStart: {
      type: Function,
    },
    onPTZStop: {
      type: Function,
    },
    onPTZCentralize: {
      type: Function,
    }
  },
  data() {
    return {
      isLoading: false, // Флаг состояния загрузки плеера/видео.
      showFatalError: false, // Флаг для показа критических ошибок.
      showArchiveAvailableError: false, // Флаг для показа ошибок доступа к архивным частям.
      lowLatencyMode: this.initialLowLatencyMode, // Флаг для включения плеера режиме низкой задержки.
      archiveMode: !!(!_.isEmpty(this.availableArchiveFragments) && this.archiveToken && this.initialTimeShift), // Флаг состояния режима проигрывания архивного видео.
      downloadMode: false, // Флаг для включения функций скачивания.
      streamNumber: this.streamCount < this.initialStreamNumber ? 1 : this.initialStreamNumber, // Номер потока для воспроизведения.
      player: null, // Объект плеера.
      keymap: {}, // Настройки горячих клавиш для управления плеером через клавиатуру.
      showSpeedMenu: false, // Флаг состояния отображения поп-апа для выбора скорости видео.
      showSettingsMenu: false, // Флаг состояния отображения поп-апа для настроек.
      singleClickOnVideo: false, // Флаг для точного отслеживания однократного ЛКМ по видео для его остановки и продолжения воспроизведения.
      timeoutIdForSingleClickOnVideo: null, // Таймер для автоматического снятия флага для точного отслеживания однократного ЛКМ по видео.
      showControls: true, // Флаг для отображения панели с элементами управления видео.
      canLLHLS: this.possibleLowLatencyHls, // Флаг для определения возможности подключения видео трансляции по технологии LLHLS.
      // Проброшенные свойства элемента video, которые не могут работать реактивно с vue.
      paused: false,
      muted: true,
      volume: 1,
      playbackRate: 1.0,
      // Свойства для взаимодействия с компонентом шкалы времени.
      timelineSize: 0, // Порядковый номер в размерах временной шкалы.
      timeShift: new Date(), // Абсолютное время воспроизведения (для архива).
      liveFrom: new Date(), // Время с которого началось воспроизведение прямой трансляции для корректного смещения timeShift.
      currentArchiveFrom: null, // Время с которого началось воспроизведение архива для корректного смещения timeShift https://git.ufanet.ru/video/camsng-frontend-shared/-/issues/19
      downloadFrom: null, // Начало запрашиваемого видео для скачивания.
      downloadTo: null, // Конец запрашиваемого видео для скачивания.
      needTimelapse: false, // Флаг для запроса скачивания видео в ускоренном режиме.
      configFlatPickr: {
        dateFormat: "d.m.Y H:i:S",
        enableSeconds: true,
        static: true,
      },
      // Свойства для работы увлечения картинки в видео и перемещения увеличенного участка видео.
      isMovingVideo: false, // Флаг для контроля перемещения.
      startMovingPageX: null, // Зафиксированное значение pageX мыши на момент начала перемещения.
      startMovingPageY: null, // Зафиксированное значение pageY мыши на момент начала перемещения.
      startMovingOffsetX: null, // Зафиксированное смещение video по X на момент начала перемещения.
      startMovingOffsetY: null, // Зафиксированное смещение video по Y на момент начала перемещения.
      sourceMatrix: [1, 0, 0, 1, 0, 0], // Исходные значения матрицы для отображения участка видео.
      // Настройки для рисования зон аналитики.
      showZonePainter: false, // Флаг состояния отображения компонента для рисования зон аналитики. Запрещает операции изменения размеров видео.
      zoneOptions: ZONES_OPTIONS,
      visibleVideoWidth: 0, // Ширина видимой части видео.
      visibleVideoHeight: 0, // Высота видимой части видео.
      selectedZoneId: 0, // Идентификатор зоны для редактирования в данный момент.
      zones: [], // Актуальные данные по зонам.
      showAllZones: false, // Флаг для показа всех зон аналитики сразу.
      showHelpForZones: false, // Флаг для показа справки по редактированию зон.
      // Настройки для восстановления после сбоя.
      disasterNumber: 0, // Количество подряд происходящих сбоев в работе плеера, которые требуют от него восстановить работу.
      delayRecovery: this.minDelayForRecovery, // Расчетная задержка в секундах перед запуском процедуры восстановления плеера после сбоя.
      showLinePainter: false, // Флаг состояния отображения компонента для рисования зон аналитики. Запрещает операции изменения размеров видео.
      lines: [], // Актуальные данные по зонам.
      showAllLines: false, // Флаг для показа всех зон аналитики сразу.
      ptzToolbarFlag: true, // Флаг для показа панели PTZ.
    };
  },
  computed: {
    /**
     * @return {Boolean} Возможность использования в плеере функций для работы с архивом.
     */
    canArchiveMode() {
      return !_.isEmpty(this.availableArchiveFragments) && this.archiveToken;
    },
    /**
     * Левый лимит временной шкалы, меньше которого архив не доступен.
     *
     * Если плеер не предназначен для проигрывания архива - расчет доступного времени для шкалы не имеет смысла.
     *
     * @return {Date}
     */
    availableArchiveFrom() {
      return this.canArchiveMode ? this.availableArchiveFragments[0][0] : null;
    },
    /**
     * Правый лимит временной шкалы, после которого архив не доступен.

     * Если плеер не предназначен для проигрывания архива - расчет доступного времени для шкалы не имеет смысла.
     * Если плеер имеет возможность переходить и к прямой трансляции и к архиву то у временной шкалы должна быть возможность отражать
     * состояние прямой трансляции - текущее время = временной сдвиг.
     * Для актуального отображения архива - нужно обеспечить (снаружи компонента) постоянное обновление доступных границ архива.
     *
     * @return {Date|null}
     */
    availableArchiveTo() {
      if (!this.canArchiveMode) {
        return null;
      }
      if (this.archiveMode) {
        return this.availableArchiveFragments[this.availableArchiveFragments.length - 1][1];
      }
      return this.timeShift;
    },
    /**
     * Вернет true если нужно отключить функции перехода к прямой трансляции.
     *
     * В случае когда между последнем временем доступного архива и текущем временем имеется длительный разрыв
     * считается прямая трансляция так же не доступна.
     *
     * @return {Boolean}
     */
    blockLiveMode() {
      return this.forceBlockLiveMode || (this.canArchiveMode && ((Date.now() - this.availableArchiveTo) > 60000)); // 1 мин = 60 000 мс
    },
    /**
     * @return {Number|null} Unix представление времени currentArchiveFrom в секундах.
     */
    unixCurrentArchiveFrom() {
      return this.currentArchiveFrom ? Math.trunc(this.currentArchiveFrom / 1000) : null;
    },
    /**
     * @return {Number} Unix представление времени availableArchiveTo в секундах.
     */
    unixAvailableArchiveTo() {
      return Math.trunc(this.availableArchiveTo / 1000);
    },
    /**
     * Параметр дельты для архива, получаемого по HLS, в секундах или "now".
     * Если возвращает секунды, то не более чем указано в {@see maxArchiveDuration}
     *
     * Флюсоник может отдавать архив в нескольких режимах.
     *
     * ## HLS Event playlist
     * EXT-X-PLAYLIST-TYPE:EVENT
     *
     * Плейлист M3U в EVENT периодически обновляется, это позволяет непрерывно воспроизводить архив как смещение от прямой трансляции.
     * Указывается начало архивного фрагмента без указания продолжительности.
     *
     * Если запрашивается архив, который по времени отстает от трансляции не более чем на {@see maxArchiveDuration},
     * то можно использовать этот режим.
     *
     * // todo не проверено если архив есть а трансляции нет.
     *
     * ## HLS playlist format for Video on Demand (VOD) (DVR catchup playback)
     * EXT-X-PLAYLIST-TYPE:VOD
     *
     * Плейлист M3U в VOD не обновляется. Строго заданы рамки архива, в которых воспроизводится видео.
     * Указывается начало запрошенного фрагмента и дельта в секундах (продолжительность).
     *
     * Так как плейлист не обновляется, то не получится использовать VOD для недавнего архива, с расчетом на то что при достижении
     * конца (на момент запроса) архива воспроизведение архива продолжится с новыми видео фрагментами.
     * В основном запросы архива должны идти через VOD с лимитированной продолжительностью. Нет смысла забирать плейлист длиной
     * за весь доступный участок архива, поскольку плейлист может получится очень длинным и тяжелым, что скажется на скорости его загрузки.
     * Так же есть вероятность попасть на битые участки архива в плейлисте, которые не удастся корректно обработать,
     * а короткие плейлисты позволят избежать частого попадания в битые участки.
     *
     * @return {String|Number}
     */
    currentArchiveDuration() {
      if (!this.expectationArchiveMode && this.unixAvailableArchiveTo) {
        // Для выключенного режима ожидания архива, всегда будет использоваться общая длительность архива
        // (на основе переданной информации об архиве).
        return this.unixCurrentArchiveFrom ? this.unixAvailableArchiveTo - this.unixCurrentArchiveFrom : 0;
      }
      // EXT-X-PLAYLIST-TYPE:EVENT
      if (Math.trunc((+this.availableArchiveTo - this.timeShift) / 1000) < this.maxArchiveDuration) {
        // Для проигрывания недавнего архива можно использовать HLS Events.
        return "now";
      }
      // EXT-X-PLAYLIST-TYPE:VOD
      // Для всех остальных случаев используется VOD с лимитированной длительностью архива,
      // по истечении которой архив будет запрошен с нового начального времени.
      return this.maxArchiveDuration;
    },
    /**
     * Источник видео для плеера.
     *
     * Выбор дорожки:
     *
     * - для HLS через ?filter=tracks:v1a1 либо /${this.cameraNumber}/tracks-v1a1/index.m3u8?
     * - для MSE установка потока требуется через API библиотеки плеера.
     * - для FLV не требуется.
     *
     * @return {String}
     */
    sourceVideoUrl() {
      let actualStreamNumber = this.streamNumber;
      if (this.vendorName === "UMS") {
        if (this.streamNumber === 1) {
          actualStreamNumber = 0;
        } else if (this.streamNumber === 2) {
          actualStreamNumber = 1;
        }
      }
      if (this.httpProtocol && this.wsProtocol && this.domain && this.cameraNumber && actualStreamNumber !== undefined) {
        if (this.archiveMode && this.archiveToken) {
          // Архивный режим.
          if (this.unixCurrentArchiveFrom && this.vendorName === "Flussonic") {
            return `${this.httpProtocol}://${this.domain}/${this.cameraNumber}/tracks-v${actualStreamNumber}a1/archive-${this.unixCurrentArchiveFrom}-${this.currentArchiveDuration}.m3u8?token=${this.archiveToken}`;
          } else {
            return `${this.httpProtocol}://${this.domain}/${this.cameraNumber}/${actualStreamNumber}/archive-${this.unixCurrentArchiveFrom}-${this.currentArchiveDuration}.m3u8?token=${this.archiveToken}`;
          }
        } else if (this.liveToken) {
          // Прямая трансляция.
          if (this.lowLatencyMode && this.vendorName === "Flussonic") {
            return `${this.wsProtocol}://${this.domain}/${this.cameraNumber}/mse_ld?realtime=true&token=${this.liveToken}`;
          }
          if (this.lowLatencyMode && this.vendorName !== "Flussonic") {
            return `${this.wsProtocol}://${this.domain}/${this.cameraNumber}/${actualStreamNumber}/mse_ld?token=${this.liveToken}`;
          }
          return this.vendorName === "Flussonic" ?
            `${this.httpProtocol}://${this.domain}/${this.cameraNumber}/tracks-v${actualStreamNumber}a1/${this.canLLHLS ? "index.ll.m3u8" : "index.m3u8"}?token=${this.liveToken}`
            : `${this.httpProtocol}://${this.domain}/${this.cameraNumber}/${actualStreamNumber}/${this.canLLHLS ? "index.ll.m3u8" : "index.m3u8"}?token=${this.liveToken}`;
        }
      }
      return "";
    },
    /**
     * @return {Boolean} Флаг определяет возможность для рисования зон аналитики.
     */
    canEditZones() {
      return this.analyticZonesConfig instanceof AnalyticZonesConfig;
    },
    canEditLines() {
      return this.analyticLinesConfig instanceof AnalyticLinesConfig;
    },
    /**
     * @return {Boolean} Вернет true, если состояние плеера перешло в режим показа ошибок.
     */
    showError() {
      return this.showFatalError || this.showArchiveAvailableError;
    },
  },
  watch: {
    /**
     * Наблюдение за временем начала воспроизведения, которое было передано в плеер.
     * При его изменении требуется скорректировать текущий временной сдвиг.
     */
    initialTimeShift() {
      this.updateCurrentTimeByTimeShift(this.initialTimeShift);
    },
    /**
     * Наблюдение за начальным состоянием флага режима низкой задержки, для его синхронизации со внутренним значением.
     */
    initialLowLatencyMode() {
      if (this.lowLatencyMode === this.initialLowLatencyMode) {
        return;
      }
      this.lowLatencyMode = this.initialLowLatencyMode;
    },
    /**
     * Наблюдение за количеством доступных потоков, чтобы в случае их смены скорректировать номер текущего потока.
     */
    streamCount() {
      this.setStreamNumber(this.streamNumber);
    },
    /**
     * Наблюдение за флагом архивного режима для сообщения родительскому компоненту актуального состояния.
     * Так же при переходе к архиву необходимо проверять его доступность, в рамках информации в плеере.
     */
    archiveMode() {
      /**
       * Отдает актуальный флаг состояния режима проигрывания архивного видео.
       *
       * @event archive-mode-update
       */
      this.$emit("archive-mode-update", this.archiveMode);
      this.checkIfArchiveAvailable();
    },
    /**
     * Смена источника видео требует его перезагрузки.
     */
    sourceVideoUrl() {
      this.reloadPlayer();
    },
    /**
     * Изменение уровня громкости.
     */
    volume() {
      this.$refs.videoPlayer.volume = this.volume;
    },
    /**
     * Включение/выключение звука.
     */
    muted() {
      this.$refs.videoPlayer.muted = this.muted;
    },
    /**
     * Переключение скорости воспроизведения.
     */
    playbackRate() {
      this.$refs.videoPlayer.playbackRate = this.playbackRate;
    },
    /**
     * Отслеживание момента прекращения перемещения video для обнуления значений для расчетов перемещения.
     */
    isMovingVideo() {
      if (!this.isMovingVideo) {
        this.startMovingPageX = this.startMovingPageY = this.startMovingOffsetX = this.startMovingOffsetY = null;
      }
    },
    /**
     * При смене времени, с которого началось воспроизведение архива, нужно проверить доступность архива.
     */
    currentArchiveFrom() {
      this.checkIfArchiveAvailable();
    },
  },
  /**
   * Проверки передаваемых параметров, которые зависят между собой и попытка связать их между собой на начальной загрузке.
   * Настройка горячих клавиш. Регистрация функции отложенного выключения интерфейса управления плеером.
   * Обработка информации для редактирования зон аналитики.
   */
  created() {
    // Если прямая трансляция закрыта, но доступен архив а при этом начальные установки не переведены в архивный режим - перевести просмотр в конец архива.
    if (this.canArchiveMode && this.blockLiveMode && !this.archiveMode) {
      this.timeShift.setTime(+this.availableArchiveTo);
    }

    // Если начальные настройки плеера переданы для работы в архивном режиме - необходимо просчитать параметры для URL.
    if (this.archiveMode) {
      let initialTimeShift = this.initialTimeShift;
      if (this.autoShiftMode && initialTimeShift < this.availableArchiveFrom) {
        // Корректировка самого сдвига, который выходит за пределы доступного архива.
        initialTimeShift = new Date(this.availableArchiveFrom);
      }
      this.timeShift = initialTimeShift;
      this.currentArchiveFrom = this.getActualArchiveFrom(initialTimeShift);
    }
    // Информирование родительского компонента о начальном состоянии режима проигрывания архивного видео.
    /**
     * Отдает актуальный флаг состояния режима проигрывания архивного видео.
     *
     * @event archive-mode-update
     */
    this.$emit("archive-mode-update", this.archiveMode);

    // Настройка горячих клавиш.
    if (this.hotKeysEnabled) {
      this.keymap = {
        "f": this.switchFullscreen,
        "j": () => {
          this.shiftCurrentTimeBySeconds(-10);
        },
        "k": () => {
          this.paused ? this.play() : this.pause();
        },
        "l": () => {
          this.shiftCurrentTimeBySeconds(+10);
        },
        "m": () => {
          if (this.hasSound) {
            this.muted = !this.muted;
          }
        },
        "space": () => {
          this.paused ? this.play() : this.pause();
        },
        "up": () => {
          if (this.hasSound) {
            this.volume = Math.min(1, this.volume + 0.1);
          }
        },
        "down": () => {
          if (this.hasSound) {
            this.volume = Math.max(0, this.volume - 0.1);
          }
        },
        "left": () => {
          this.shiftCurrentTimeBySeconds(-5);
        },
        "right": () => {
          this.shiftCurrentTimeBySeconds(+5);
        },
      };
    }

    // Обработка информации для редактирования зон аналитики.
    if (this.canEditZones) {
      for (const i in ZONES_OPTIONS) {
        const zoneOption = ZONES_OPTIONS[i],
          zoneId = zoneOption.id;

        let currentZoneConfig = new AnalyticZone();
        if (zoneId in this.analyticZonesConfig.zones) {
          currentZoneConfig = this.analyticZonesConfig.zones[zoneId];
        }
        this.zones.push(currentZoneConfig);
      }
    }

    if (this.canEditLines) {
      let currentLineConfig = new AnalyticLine();
      currentLineConfig = this.analyticLinesConfig.lines[0];

      this.lines.push(currentLineConfig);


    }
    // Отложенное сокрытие интерфейса - полезно при неподвижной мышке на видео.
    this.debouncedHideControls = _.debounce(this.hideControls, 3000);
    this.showThenHideControls();
    // Отложенное включение плеера, чтобы не наслоить несколько вызовов, происходящих в одно время из разных мест.
    this.throttledSwitchOn = _.throttle(this.switchOn, 300, {trailing: false});
    // Отправка уведомления в родительский компонент об изменении времени проигрывания не чаще 1 раза в сек.
    this.throttledEmitTimeShiftUpdate = _.throttle(() => {
      /**
       * По аналогии с `@timeupdate` в `<video>` (которое происходит примерно 4 раза в секунду),
       * это событие отправляет актуальное, абсолютное время воспроизведения типа `date`,
       * но с частотой в 1 сек.
       *
       * @event time-shift-update
       */
      this.$emit("time-shift-update", this.timeShift);
    }, 1000);
  },
  /**
   * Автоматическое включение плеера после появления элемента video.
   * Регистрация глобального слушателя для отпускания ЛКМ для отключения отслеживания перемещения элементов на видео.
   */
  mounted() {
    this.throttledSwitchOn();
    window.addEventListener("mouseup", this.onMouseUpOnVideo);
  },
  /**
   * Уничтожение компонента требует явного уничтожения всех вендорных привязок с плеером.
   * Удаление глобального слушателя отпускания ЛКМ.
   */
  beforeDestroy() {
    clearTimeout(this.recoveryTimerId);
    this.switchOff();
    window.removeEventListener("mouseup", this.onMouseUpOnVideo);
  },
  methods: {
    /**
     * Проброс методов PTZ для управления камерой.
     */
    handlePTZStart(direction) {
      this.onPTZStart(direction);
    },
    handlePTZStop() {
      this.onPTZStop();
    },
    handleCentralizePTZ() {
      this.onPTZCentralize();
    },
    /**
     * Включение плеера.
     * Создание объекта отвечающего за взаимодействие с библиотекой для воспроизведения потокового видео.
     *
     * @return {Promise}
     */
    switchOn() {
      if (!this.$refs.videoPlayer || !this.sourceVideoUrl) {
        // Без элемента video и без вменяемого источника видео, запустить плеер невозможно,
        // но это может быть вполне легитимное поведение поэтому именно на данном этапе отправлять ошибки нецелесообразно.
        return Promise.resolve();
      }
      const onError = () => {
        devLog("[Player error]", this.lowLatencyMode && !this.archiveMode, this.streamNumber);
        this.showFatalError = true;
        this.isLoading = false;
        this.disasterRecovery();
      };

      this.showFatalError = false;
      this.isLoading = true;
      return Promise.resolve()
        .then(() => {
          // Не создается сессия на флюсонике если в запросе длинный referer https://git.ufanet.ru/video/camsng-frontend-shared/-/issues/1
          // Обходится через обрезанный запрос.
          if (!this.lowLatencyMode || this.archiveMode && this.vendorName === "Flussonic") {
            return fetch(this.sourceVideoUrl, {method: "HEAD", referrerPolicy: "no-referrer"});
          }
        })
        .then((response) => {
          // Ответ с неудачным статус кодом трактуется как ошибка.
          // todo в принципе можно сократить количество перезагрузок если сервер конкретно ответил 403 и сразу уйти на родительский компонент.
          if ((response instanceof Response) && (response.status !== 200)) {
            throw response.status;
          }
        })
        .catch((error) => {
          // Перехват ошибок на сети и с неудачным статусом.
          devLog("[Fetch flussonic error]", error);
          if (!this.lowLatencyMode && !this.archiveMode) {
            // Если произошла ошибка доступа к LLHLS, то пытаться дальше не стоит. Лучше сразу перейти на обычный HLS.
            this.canLLHLS = false;
          }
          onError();
          return true;
        })
        .then((ifFetchError) => {
          if (ifFetchError) {
            return;
          }
          this.player =
            (this.lowLatencyMode && !this.archiveMode &&  this.vendorName === "Flussonic") ?
              new PlayerMSE(this.$refs.videoPlayer, this.sourceVideoUrl, onError) :

              (this.lowLatencyMode && !this.archiveMode && this.vendorName !== "Flussonic") ?
                new PlayerMSEManul(this.$refs.videoPlayer, this.sourceVideoUrl) :

                new PlayerHLS(this.$refs.videoPlayer, this.sourceVideoUrl, onError, this.archiveMode);
          // Восстановление параметров звука и скорости воспроизведения при запуске с учетом предыдущих настроек.
          this.$refs.videoPlayer.muted = this.muted;
          this.$refs.videoPlayer.volume = this.volume;
          if (this.archiveMode) {
            this.$refs.videoPlayer.playbackRate = this.playbackRate;
          } else {
            this.playbackRate = this.$refs.videoPlayer.playbackRate = 1;
          }

          // Попытка автоматического воспроизведения.
          this.paused = this.$refs.videoPlayer.paused;
          if (this.paused && !this.archiveMode) {
            this.play();
          }
          // Корректировка текущего воспроизведения для архива - переход к тому что запросил пользователь.
          if (this.archiveMode) {
            this.updateCurrentTimeByTimeShift(this.timeShift);
          }
        })
        .catch((error) => {
          devLog("[Switch on player error]", error);
          onError();
        });
    },
    /**
     * Выключение плеера.
     *
     * @return {Promise}
     */
    switchOff() {
      this.isLoading = false;
      return this.player && this.player.switchOff();
    },
    /**
     * Последовательное выключение плеера, уничтожение всех привязок и его повторный запуск.
     *
     * Асинхронность нужна т.к. внутри библиотек плеера выключение происходит асинхронно и в результате отключение/включение
     * может происходить непоследовательно что приведет к нерабочему состоянию.
     */
    async reloadPlayer() {
      await this.switchOff();
      this.throttledSwitchOn();
    },
    /**
     * Включение проигрывания видео актуального потока.
     */
    play() {
      if (!this.player) {
        return;
      }
      this.player.play(this.streamNumber);
      this.paused = false;
    },
    /**
     * Приостановка проигрывания видео.
     */
    pause() {
      if (!this.player) {
        return;
      }
      this.player.pause();
      this.paused = true;
    },
    /**
     * Установка номера потока для воспроизведения. Номер потока будет скорректирован в соответствии с доступным количеством потоков.
     * В условиях воспроизведения прямой трансляции с низкой задержкой (MSE) необходимо вызывать специальный метод у библиотеки плеера.
     *
     * @param {Number} currentStreamNumber
     */
    setStreamNumber(currentStreamNumber) {
      this.streamNumber = currentStreamNumber;

      if (this.player instanceof PlayerMSE) {
        this.player.setTrack(currentStreamNumber);
      }
    },
    /**
     * Установит локальное значение скорости воспроизведения.
     *
     * @param {Number} playbackRate
     */
    setPlaybackRate(playbackRate) {
      this.playbackRate = playbackRate;
    },
    /**
     * Сдвинет время в плеере на указанное количество секунд от текущей позиции timeShift.
     *
     * @param {Number} secondsForShift положительное или отрицательное количество секунд, с которым будет суммироваться время сдвига.
     */
    shiftCurrentTimeBySeconds(secondsForShift) {
      const newTimeShift = new Date(this.timeShift);
      newTimeShift.setSeconds(newTimeShift.getSeconds() + secondsForShift);
      this.updateCurrentTimeByTimeShift(newTimeShift);
    },
    /**
     * Установит время в плеере в то, что было указано в календаре.
     */
    shiftCurrentTimeViaCalendar(timeShift) {
      this.updateCurrentTimeByTimeShift(timeShift);
      this.showTimeShift();
    },
    /**
     * Обновление текущего времени воспроизведения элемента video.
     * Обновлять время необходимо в случаях установки нового значения времени на временной шкале и при начальной загрузке плеера.
     *
     * Если плеер не находится в состоянии проигрывания архива, но пользователь устанавливает время желаемого просмотра (в прошлом),
     * это означает необходимость перехода плеера к архивному состоянию. Но переход к архиву провоцирует создание нового плеера,
     * у которого (после создания), нужно скорректировать время воспроизведения. Поэтому именно переход к архиву завершает работу функции,
     * а ее саму следует вызвать повторно после создания архивного плеера для коррекции.
     *
     * Переход к участку дальше чем доступный архив провоцирует переход к прямой трансляции.
     * А если переход к ней заблокирован, то автоматически в конец архива
     *
     * @param {Date} newTimeShift
     */
    updateCurrentTimeByTimeShift(newTimeShift) {
      const newUnixTimeShift = Math.trunc(newTimeShift / 1000);
      if (!this.blockLiveMode && ((this.unixAvailableArchiveTo && (this.unixAvailableArchiveTo < newUnixTimeShift)) || !newTimeShift)) {
        this.goToLive();
        return;
      }

      if (this.autoShiftMode && newTimeShift < this.availableArchiveFrom) {
        // Корректировка самого сдвига, который выходит за пределы доступного архива.
        newTimeShift = new Date(this.availableArchiveFrom);
      }
      const actualArchiveFrom = this.getActualArchiveFrom(newTimeShift);
      if ((+actualArchiveFrom !== +this.currentArchiveFrom) || !this.archiveMode) {
        this.archiveMode = true;
        this.currentArchiveFrom = actualArchiveFrom;
        this.timeShift = newTimeShift;
        // После таких манипуляций плеер будет пересоздан с новым источником видео и потребуется вновь скорректировать время видео.
        return;
      }
      if (!this.currentArchiveFrom) {
        // Если архив недоступен, визуально нужно все еще отобразить изменения на шкале, а потому фиксируем временной сдвиг.
        this.timeShift = newTimeShift;
        return;
      }

      let newCurrentTime = newUnixTimeShift - this.unixCurrentArchiveFrom;
      if (newCurrentTime < 0) {
        newCurrentTime = 0;
      }
      this.$refs.videoPlayer.currentTime = newCurrentTime;
    },
    /**
     * Вызывается по событию `@canplay` элемента video, когда тот может начать воспроизведение.
     * Срабатывает в том числе после изменения времени начала воспроизведения (seek).
     *
     * Для случаев начальной загрузки выключается индикатор загрузки и отображается видео.
     * При переходе к прямой трансляции необходимо зафиксировать этот момент времени,
     * чтобы корректировать смещение времени для "не архивного" состояния.
     */
    onCanPlay() {
      this.isLoading = false;
      this.showFatalError = false;
      // Если видео может проигрываться, то можно спокойно сбросить настройки восстановления после сбоев.
      this.disasterNumber = 0;
      this.delayRecovery = this.minDelayForRecovery;
      clearTimeout(this.recoveryTimerId);

      if (!this.archiveMode) {
        // При старте воспроизведения трансляции плеер может отдавать начальное время воспроизведения отличное от 0, часто такое происходит с HLS.
        // Поэтому, чтобы не создавать большой разрыв между индикатором прямой трансляции и архивом, нужна коррекция.
        const correction = this.$refs.videoPlayer ? this.$refs.videoPlayer.currentTime * 1000 : 0;
        this.liveFrom = new Date(Date.now() - correction);
      }
      // Имеет смысл вызвать метод актуализации временного сдвига именно после нашего официального окончания загрузки.
      this.onTimeUpdate();
    },
    /**
     * Вызывается по событию `@timeupdate` элемента video, когда тот обновляет свое свойство currentTime с float количеством секунд
     * от начала воспроизведения видео фрагмента.
     *
     * Выполняется обновление показателя смещения времени {@see timeShift}.
     * timeShift - это значение типа Date, хранит абсолютное время, которое отображается на шкале.
     * Чтобы рассчитать актуальное значение, необходимо взять текущий currentTime (который считается от 0 - начало видео)
     * и сложить его с моментом начала участка видео (unix).
     *
     * Для прямой трансляции время начала проигрывания невозможно точно определить из-за того что видео потоковое (duration = Infinity).
     * Для этого момент начала воспроизведения трансляции фиксируется в компоненте {@see liveFrom}.
     * Для архива абсолютное время начала участка видео всегда известно, оно определяется пользователем когда тот указывает время архива
     * и оно фиксируется в URL источника видео.
     */
    onTimeUpdate() {
      if (this.isLoading || (this.archiveMode && !this.currentArchiveFrom)) {
        // Предотвращение любых случайных изменений в процессе загрузки плеера, которые могут возникнуть между разными состояниями.
        // А так же если на предыдущем этапе изменения времени плеера было установлено, что архив недоступен,
        // значит и дальнейший расчет времени тоже не имеет смысла.
        return;
      }
      // Актуализация состояния паузы плеера.
      this.paused = this.$refs.videoPlayer ? this.$refs.videoPlayer.paused : this.paused;

      const currentTime = this.$refs.videoPlayer ? this.$refs.videoPlayer.currentTime : 0,
            correction = this.archiveMode ? +this.currentArchiveFrom : +this.liveFrom;
      this.timeShift = new Date(correction + currentTime * 1000);

      if (this.archiveMode) {
        const actualArchiveFrom = this.getActualArchiveFrom(this.timeShift);
        if ((currentTime > this.maxArchiveDuration) || (+this.timeShift !== +actualArchiveFrom)) {
          // Для архива абсолютное время начала участка видео следует скорректировать либо когда время воспроизведения превысило максимальное,
          // либо когда следует сменить доступный участок архива.
          this.currentArchiveFrom = actualArchiveFrom;
        }
      }

      // Если при просмотр архива текущее воспроизведение подходит к концу, то происходит переключение на прямую трансляцию, если она доступна.
      if (this.archiveMode && (!this.blockLiveMode && (this.timeShift >= this.availableArchiveTo))) {
        this.goToLive();
      }

      this.throttledEmitTimeShiftUpdate();
    },
    /**
     * Простой перевод плеера из архивного режима в режим воспроизведения прямой трансляции.
     */
    goToLive() {
      this.archiveMode = false;
    },
    /**
     * Вызывается по событию `@onerror` элемента video, когда тот получает системную ошибку браузера.
     * Если ошибка была получена с опозданием когда элемента video уже нет, нет смысла ее эскалировать.
     *
     * @param {MediaError} mediaError
     */
    onError(mediaError) {
      if (!this.$refs.videoPlayer) {
        return;
      }
      devLog("[Video error]", mediaError);
      this.showFatalError = true;
      this.disasterRecovery();
    },
    /**
     * Комплексный метод для реагирования на ошибки и запуска процедур восстановления после сбоя.
     * Восстановление работы плеера сводится к простому перезапуску его работы, через отключение и с задержкой последующее включение.
     * Выдержка между перезапусками нарастает экспоненциально, но не превышает заданных в настройках.
     */
    disasterRecovery() {
      // Ошибки по разным причинам в одно и то же время не нужно накапливать. Восстановление следует проводить постепенно.
      if (this.recoveryTimerId) {
        return;
      }

      this.disasterNumber++;
      if (this.disasterNumber > this.maxDisasters) {
        /**
         * Событие при сбое в работе плеера, после которого сам плеер не смог восстановить свою нормальную работу.
         * Отправляется если количество подряд идущих сбоев превысило допустимое количество.
         *
         * @event disaster
         */
        this.$emit("disaster");
        this.showFatalError = true;
        this.switchOff();
        return;
      }

      this.delayRecovery = Math.min(this.delayRecovery * this.disasterNumber, this.maxDelayForRecovery);
      this.delayRecovery = this.delayRecovery + Math.random();

      // Планирование восстановления с определенной задержкой.
      this.switchOff();
      this.isLoading = true;
      this.recoveryTimerId = setTimeout(() => {
        devLog("[Player recovery]", this.disasterNumber, this.delayRecovery);
        this.recoveryTimerId = null;
        this.switchOn();
      }, this.delayRecovery * 1000);
    },
    /**
     * Функция проверит возможность воспроизвести архив на основе времени начала воспроизведения архива {@see currentArchiveFrom}.
     * Если оно = null, то это означает, что определить его невозможно и вместе с этим авто смещения по архиву не предусмотрено.
     * В таком случае архив в этом плеере показать нельзя, остается только выключить плеер и сообщить об этом пользователю.
     * В противном случае плеер необходимо перезагрузить.
     */
    checkIfArchiveAvailable() {
      if (this.currentArchiveFrom) {
        this.showArchiveAvailableError = false;
      } else {
        if (this.archiveMode) {
          this.showArchiveAvailableError = true;
          this.switchOff();
        } else {
          this.showArchiveAvailableError = false;
          this.reloadPlayer();
        }
      }
    },
    /**
     * Вернет значение archiveFrom актуального из доступных видео фрагментов по переданному сдвигу.
     *
     * Если переданный временной сдвиг:
     * 0. за пределами доступных участков (между ними) и режим авто сдвига выключен, то вернется null;
     * 1. за пределами доступных участков (между ними) и режим авто сдвига включен, то вернется время = начало следующего участка;
     * 2. укладывается в какой-либо из доступных участков (читай находится в границах конкретного участка архива), то проверяется:
     * укладываются ли переданный временной сдвиг в те рамки, в которых сейчас воспроизводится видео, определенные параметрами
     * {@see currentArchiveFrom} и {@see currentArchiveDuration}.
     * И если да, то вернется текущее значение archiveFrom = {@see currentArchiveFrom} (чтобы не происходило перезапроса видео),
     * но если нет, то вернется собственно сам сдвиг (с которого и должно стартовать новое воспроизведение архивного участка).
     *
     * @param {Date} timeShift
     * @return {Date|null}
     */
    getActualArchiveFrom(timeShift) {
      let actualArchiveFrom = this.autoShiftMode ? this.availableArchiveFrom : null,
          useNext = false;
      for (const [fragmentStart, fragmentEnd] of this.availableArchiveFragments) {
        if ((fragmentStart <= timeShift) && (timeShift <= fragmentEnd)) {
          // Попытка избежать изменения URL для архива через проверку попадания timeShift в текущие пределы запрошенного архива.
          // Т.е. если на момент текущего расчета плеер не в архиве,
          // то считаем что мы не имеем возможности перейти к видео без перезагрузки плеера.
          if (this.archiveMode && this.currentArchiveFrom !== null) {
            if ((+fragmentStart <= +this.currentArchiveFrom) && (+this.currentArchiveFrom <= +timeShift)) {
              // В случае если архив очень дырявый (несколько пропусков в на расстоянии {@see maxArchiveDuration}), то лучше перезапросить
              // видео когда доступный участок пересекается по времени с запрошенным фрагментом.
              if (_.isString(this.currentArchiveDuration)) {
                // Если просматривается недавний архив и текущее начало участка видео было установлено раньше чем то, что запросил пользователь.
                // В таком случае видео можно запросить без запроса новых фрагментов.
                return this.currentArchiveFrom;
              } else {
                const currentArchiveTo = new Date(+this.currentArchiveFrom + (this.currentArchiveDuration * 1000));
                if (+timeShift < +currentArchiveTo) {
                  // Если просматривается дальний архив и запрашивается фрагмент, который находится между
                  // ранее установленным началом и расчетным концом текущего участка.
                  // В таком случае видео можно запросить без запроса нового участка видео.
                  return this.currentArchiveFrom;
                }
              }
            }
          }
          return timeShift;
        }

        if (this.autoShiftMode) {
          // Когда надо вернуть сдвиг = началу следующего доступного участка, чтобы автоматом перейти к нему.
          if (fragmentStart <= timeShift || useNext) {
            actualArchiveFrom = fragmentStart;
          }
          useNext = fragmentEnd <= timeShift;
        }
      }
      return actualArchiveFrom;
    },
    /**
     * Отслеживание и фиксирование изменения границ для скачивания.
     */
    changeDownloadBorders([downloadFrom, downloadTo]) {
      this.downloadFrom = downloadFrom;
      this.downloadTo = downloadTo;
    },
    /**
     * Проверка возможности скачать видео при заданных параметрах и запуск процесса по получению URL для скачивания.
     *
     * Сам плеер не инициирует процесс скачивания, поскольку для этого нужен особый токен.
     * Чтобы его получить нужно заранее знать начало запрашиваемого фрагмента и его продолжительность.
     * Поэтому функция выбросит событие `@download-video` с необходимым набором параметров, для запуска процесса скачивания.
     *
     * Часть логики по определению типа скачиваемого файла заложена здесь заранее:
     *
     * - если видео длительностью более 1 часа, без ускорения - .ts
     * - если видео длительностью менее 1 часа либо более 1 часа, но с ускорением - .mp4
     */
    downloadVideo() {
      const unixDownloadFrom = Math.trunc(this.downloadFrom / 1000),
            unixDownloadTo = Math.trunc(this.downloadTo / 1000),
            unixDownloadDelta = unixDownloadTo - unixDownloadFrom;

      let downloadType = DOWNLOADS_TYPES.TS,
          timelapseDuration = "";
      if (unixDownloadDelta <= 3600) {
        downloadType = DOWNLOADS_TYPES.MP4;
      }
      if (this.needTimelapse) {
        downloadType = DOWNLOADS_TYPES.MP4;
        timelapseDuration = this.needTimelapse ? `timelapse=${Math.round(unixDownloadDelta / 50)}&` : "";
      }

      const getterDownloadUrl = (tokenDownload) => {
        return `${this.httpProtocol}://${this.domain}/${this.cameraNumber}/archive-${unixDownloadFrom}-${unixDownloadDelta}.${downloadType.toLowerCase()}?${timelapseDuration}token=${tokenDownload}`;
      };

      /**
       * Событие при запросе скачивания видео. Передает массив параметров:
       *
       * - функция, которая принимает в качестве аргумента токен, а возвращает готовый URL для скачивания;
       * - unix метку начала запрашиваемого фрагмента;
       * - продолжительность запрашиваемого видео в секундах;
       *
       * @event download-video
       */
      this.$emit("download-video", [getterDownloadUrl, unixDownloadFrom, unixDownloadDelta]);
    },
    /**
     * Отслеживание `@mousedown` для установки флагов отслеживания активностей работы с видео
     * и регистрация обработчика для событий перемещения мыши на видео.
     */
    onMouseDownOnVideo() {
      this.singleClickOnVideo = this.isMovingVideo = true;
      this.timeoutIdForSingleClickOnVideo = setTimeout(() => {
        this.singleClickOnVideo = false;
      }, 333);
      window.addEventListener("mousemove", this.moveVideo);
    },
    /**
     * Отслеживание `@mouseup` для прекращения отслеживания активностей работы с видео и удаления обработчика перемещения мыши на видео.
     * Если зафиксирован флаг клика ЛКМ, то необходимо имитировать работу кнопки play.
     */
    onMouseUpOnVideo() {
      if (this.singleClickOnVideo) {
        this.singleClickOnVideo = false;
        clearTimeout(this.timeoutIdForSingleClickOnVideo);
        this.paused ? this.play() : this.pause();
      }
      this.isMovingVideo = false;
      window.removeEventListener("mousemove", this.moveVideo);
    },
    /**
     * Включение/выключение полноэкранного режима.
     * После перехода к полноэкранному режиму необходимо корректно отобразить временную шкалу.
     * Если до полноэкранного режима видимая часть шкалы находится близко к ее краю, то после перехода за пределами шкалы может быть
     * показана пустая неотрендереная область, для избежания этого нужен прямой вызов метода временной шкалы для ее ререндера.
     */
    async switchFullscreen() {
      if (this.showZonePainter) {
        return;
      }
      document.fullscreenElement === this.$el || document.webkitFullscreenElement === this.$el ? await exitFullscreen() : await requestFullscreenByElement(this.$el);
      this.$refs.smartPlayerTimeline && this.$refs.smartPlayerTimeline.rerenderSeekBar();
    },
    /**
     * Прокрутка колесика мыши на видео с целью увеличить картинку.
     *
     * Увеличение (уменьшение) идет с постоянным коэффициентом, но существует ограничение допустимого масштабирования.
     * Без него картинка может уменьшиться и стать невидимой.
     *
     * При уменьшении картинки может пересчитаться смещение, чтобы не создавалась пустая область без видео.
     *
     * Расчет координат смещения при изменении масштаба требует чтобы центр трансформации (0,0) был смещен
     * из центра фигуры (по умолчанию) в левый верхний угол (классический вариант) средствами CSS (transform-origin: 0 0).
     *
     * @param {WheelEvent} wheelEvent
     */
    zoomVideo(wheelEvent) {
      if (this.showZonePainter) {
        return;
      }
      const videoParam = this.$refs.videoPlayer.getBoundingClientRect(),
            screenParam = this.$refs.videoPlayer.parentElement.getBoundingClientRect(),
            oldScale = this.sourceMatrix[0], // Текущий масштаб.
            oldMouseVideoX = wheelEvent.pageX - videoParam.left, // Значение по X для мыши относительно video в текущем масштабе.
            oldMouseVideoY = wheelEvent.pageY - videoParam.top, // Значение по Y для мыши относительно video в текущем масштабе.
            // Замечание: при расчете актуальной ширины/высоты может накапливаться ошибка в естественном масштабировании,
            // так наблюдается расхождение высоты в несколько пикселей screenParam.height * oldScale != videoParam.height,
            // что приводит к неверным дальнейшим расчетам, поэтому используем единый подход и сами все считаем.
            oldVideoWidth = screenParam.width * oldScale, // Ширина элемента video в текущем масштабе.
            oldVideoHeight = screenParam.height * oldScale, // Высота элемента video в текущем масштабе.
            relativeMouseVideoX = oldMouseVideoX / oldVideoWidth, // Относительный коэффициент координаты X мыши к ее текущей ширине.
            relativeMouseVideoY = oldMouseVideoY / oldVideoHeight, // Относительный коэффициент координаты Y мыши к ее текущей высоте.
            zoomIntensity = 0.1, // Постоянный коэффициент изменения масштаба.
            deltaScale = -1 * Math.sign(wheelEvent.deltaY) * zoomIntensity, // Значение коэффициента с учетом знака.
            newScale = oldScale + deltaScale, // Новый масштаб.
            newVideoWidth = screenParam.width * newScale, // Ширина элемента video в новом масштабе.
            newVideoHeight = screenParam.height * newScale, // Высота элемента video в новом масштабе.
            newMouseVideoX = newVideoWidth * relativeMouseVideoX, // Значение по X для мыши относительно video в новом масштабе.
            newMouseVideoY = newVideoHeight * relativeMouseVideoY, // Значение по Y для мыши относительно video в новом масштабе.
            oldOffsetX = this.sourceMatrix[4],
            oldOffsetY = this.sourceMatrix[5],
            newOffsetX = oldOffsetX + (oldMouseVideoX - newMouseVideoX),
            newOffsetY = oldOffsetY + (oldMouseVideoY - newMouseVideoY);

      this.saveMatrix(newScale, newOffsetX, newOffsetY);
    },
    /**
     * Перемещение мыши при зажатой ЛКМ для перетаскивания картинки в увеличенном видео.
     *
     * Через изменение стиля transform: matrix() происходит масштабирование и смещение элемента относительно центра этого элемента.
     * Результат расчета новых значений для смещения центра картинки будет применен если он попадает в допустимые пределы перемещения
     * в контейнере с учетом масштабирования. В противном случае возможны пустые области, на которых уже события не отслеживаются.
     *
     * @param {MouseEvent} mouseEvent
     */
    moveVideo(mouseEvent) {
      // Ограничение перемещения для не отмасштабированной картинки или при отсутствии флага перемещения.
      const scale = this.sourceMatrix[0];
      if (!this.isMovingVideo || (scale <= 1)) {
        return;
      }

      // Первое движение после включения возможности перемещения картинки устанавливает начальные значения (точку отсчета) для расчетов.
      if ((this.startMovingPageX === null) || (this.startMovingPageY === null) || (this.startMovingOffsetX === null) || (this.startMovingOffsetY === null)) {
        this.startMovingPageX = mouseEvent.pageX;
        this.startMovingOffsetX = this.sourceMatrix[4];
        this.startMovingPageY = mouseEvent.pageY;
        this.startMovingOffsetY = this.sourceMatrix[5];
      }

      const newOffsetX = this.startMovingOffsetX - (this.startMovingPageX - mouseEvent.pageX),
            newOffsetY = this.startMovingOffsetY - (this.startMovingPageY - mouseEvent.pageY);

      this.saveMatrix(scale, newOffsetX, newOffsetY);
    },
    /**
     * Сохранение матрицы для отображения участка видео.
     * Сохранение (и дальнейшее применение) допускается при соблюдении ограничений области перемещения видео в допустимых пределах.
     *
     * @param {Number} scale
     * @param {Number} offsetX
     * @param {Number} offsetY
     */
    saveMatrix(scale, offsetX, offsetY) {
      const screenParam = this.$refs.videoPlayer.parentElement.getBoundingClientRect(),
            limitOffsetX = -Math.round(screenParam.width * scale - screenParam.width),
            limitOffsetY = -Math.round(screenParam.height * scale - screenParam.height);

      offsetX = offsetX >= 0 ? 0 : offsetX;
      offsetX = offsetX < limitOffsetX ? limitOffsetX : offsetX;
      offsetY = offsetY >= 0 ? 0 : offsetY;
      offsetY = offsetY < limitOffsetY ? limitOffsetY : offsetY;

      if ((scale >= 1) && (scale <= 10)) {
        this.sourceMatrix = [scale, 0, 0, scale, offsetX, offsetY];
      }
    },
    /**
     * Прямой вызов метода временной шкалы для однократного отображения индикатора текущего прогресса воспроизведения.
     */
    showTimeShift() {
      this.$refs.smartPlayerTimeline && this.$refs.smartPlayerTimeline.setFollowTimeShift();
    },
    /**
     * Перезагрузка страницы с плеером. Действие доступно при возникновении ошибки с плеером.
     */
    reloadPage() {
      document.location.reload();
    },
    /**
     * Показать элементы управления плеером и вызвать функцию отложенного скрытия.
     */
    showThenHideControls() {
      this.showControls = true;
      this.debouncedHideControls();
    },
    /**
     * Сокрытие элементов управления плеером, если все внутренние элементы скрыты.
     */
    hideControls() {
      if (this.showSettingsMenu) {
        return;
      }
      this.showControls = false;
    },
    switchLinePainter() {
      this.saveMatrix(1, 0, 0);
      this.calculateVisibleVideoBoundsLines();
      this.showLinePainter = !this.showLinePainter;
    },
    /**
     * Расчет размеров видимой части видео и масштабирование в соответствии с этим точек на зонах.
     * Размеры блока video учитывают фактическую ширину и высоту блока, а так же имеют данные о реальной ширине и высоте кадра видео.
     * Если они не совпадают по размеру - кадр масштабируется, а незанятая область закрашивается фоновым цветом.
     * Таким образом размер отмасштабированного кадра неизвестен.
     */
    calculateVisibleVideoBoundsLines() {
      if (!this.$refs.videoPlayer || !this.canEditLines) {
        return;
      }
      const screenParam = this.$refs.videoPlayer.parentElement.getBoundingClientRect(),
            screenWidth = screenParam.width,
            screenHeight = screenParam.height,
            screenRate = screenWidth / screenHeight,
            videoWidth = this.$refs.videoPlayer.videoWidth,
            videoHeight = this.$refs.videoPlayer.videoHeight,
            videoRate = videoWidth / videoHeight;

      // Если соотношение сторон в видео и экране равны, то можно сразу брать размеры экрана.
      let visibleVideoWidth = screenWidth,
          visibleVideoHeight = screenHeight;
      if (videoRate < screenRate) {
        // Если соотношение стороны в видео меньше чем в экране,
        // значит в масштабе видео границы сверху и снизу будут упираться в экран, а слева и справа останутся черные полосы.
        const scale = screenHeight / videoHeight;
        visibleVideoWidth = videoWidth * scale;
      } else if (videoRate > screenRate) {
        // Если соотношение стороны в видео больше чем в экране,
        // значит в масштабе видео границы слева и справа будут упираться в экран, а сверху и снизу останутся черные полосы.
        const scale = screenWidth / videoWidth;
        visibleVideoHeight = videoHeight * scale;
      }
      this.visibleVideoWidth = Math.trunc(visibleVideoWidth);
      this.visibleVideoHeight = Math.trunc(visibleVideoHeight);
      // Масштабирование точек на зонах.
      if (this.analyticLinesConfig.linesWidth && this.analyticLinesConfig.linesHeight) {
        // Если ранее не было установлено никаких зон в аналитике то ширина и высота для зон будут пустыми (или = 0).
        if (this.visibleVideoWidth !== this.analyticLinesConfig.linesWidth || this.visibleVideoHeight !== this.analyticLinesConfig.linesHeight) {
          // Если размеры зон не совпадают с теми что были ранее заданы, необходим пересчет положения точек по новому масштабу.
        }
      }
      this.analyticLinesConfig.linesWidth = this.visibleVideoWidth;
      this.analyticLinesConfig.linesHeight = this.visibleVideoHeight;
    },
    /**
     * Передаст все данные о зонах родителю для сохранения.
     */
    saveAllLines() {
      /**
       * Отдает объект `AnalyticLinesConfig` со всеми актуальными настройками по зонам.
       * @event save-lines
       */
      this.switchLinePainter();
      this.analyticLinesConfig.lines = this.lines;
      this.$emit("save-lines", this.analyticLinesConfig);
    },
    /**
     * Переключает режим плеера для рисования зон для видеоаналитики.
     * При переключении режима необходимо пересчитать размеры окна для рисования зон, чтобы всегда иметь актуальные данные по точкам на зонах.
     * Режим предполагает исходный масштаб видео для экрана и одновременный запрет изменения масштаба в процессе рисования зон.
     */
    switchZonePainter() {
      this.saveMatrix(1, 0, 0);
      this.calculateVisibleVideoBounds();
      this.showZonePainter = !this.showZonePainter;
      this.showHelpForZones = false;
    },
    /**
     * Отображение панели PTZ.
     */
    showPtzTooBar() {
      this.ptzToolbarFlag = !this.ptzToolbarFlag;
    },
    /**
     * Расчет размеров видимой части видео и масштабирование в соответствии с этим точек на зонах.
     * Размеры блока video учитывают фактическую ширину и высоту блока, а так же имеют данные о реальной ширине и высоте кадра видео.
     * Если они не совпадают по размеру - кадр масштабируется, а незанятая область закрашивается фоновым цветом.
     * Таким образом размер отмасштабированного кадра неизвестен.
     */
    calculateVisibleVideoBounds() {
      if (!this.$refs.videoPlayer || !this.canEditZones) {
        return;
      }
      const screenParam = this.$refs.videoPlayer.parentElement.getBoundingClientRect(),
            screenWidth = screenParam.width,
            screenHeight = screenParam.height,
            screenRate = screenWidth / screenHeight,
            videoWidth = this.$refs.videoPlayer.videoWidth,
            videoHeight = this.$refs.videoPlayer.videoHeight,
            videoRate = videoWidth / videoHeight;

      // Если соотношение сторон в видео и экране равны, то можно сразу брать размеры экрана.
      let visibleVideoWidth = screenWidth,
          visibleVideoHeight = screenHeight;
      if (videoRate < screenRate) {
        // Если соотношение стороны в видео меньше чем в экране,
        // значит в масштабе видео границы сверху и снизу будут упираться в экран, а слева и справа останутся черные полосы.
        const scale = screenHeight / videoHeight;
        visibleVideoWidth = videoWidth * scale;
      } else if (videoRate > screenRate) {
        // Если соотношение стороны в видео больше чем в экране,
        // значит в масштабе видео границы слева и справа будут упираться в экран, а сверху и снизу останутся черные полосы.
        const scale = screenWidth / videoWidth;
        visibleVideoHeight = videoHeight * scale;
      }
      this.visibleVideoWidth = Math.trunc(visibleVideoWidth);
      this.visibleVideoHeight = Math.trunc(visibleVideoHeight);

      // Масштабирование точек на зонах.
      if (this.analyticZonesConfig.zonesWidth && this.analyticZonesConfig.zonesHeight) {
        // Если ранее не было установлено никаких зон в аналитике то ширина и высота для зон будут пустыми (или = 0).
        if (this.visibleVideoWidth !== this.analyticZonesConfig.zonesWidth || this.visibleVideoHeight !== this.analyticZonesConfig.zonesHeight) {
          // Если размеры зон не совпадают с теми что были ранее заданы, необходим пересчет положения точек по новому масштабу.
          const scaleX = this.visibleVideoWidth / this.analyticZonesConfig.zonesWidth,
                scaleY = this.visibleVideoHeight / this.analyticZonesConfig.zonesHeight;
          this.zones = this.zones.map((zone) => {
            zone.points = zone.points.map((point) => {
              return {
                x: point.x * scaleX,
                y: point.y * scaleY,
              };
            });
            return zone;
          });
        }
      }
      this.analyticZonesConfig.zonesWidth = this.visibleVideoWidth;
      this.analyticZonesConfig.zonesHeight = this.visibleVideoHeight;
    },
    /**
     * Передаст все данные о зонах родителю для сохранения.
     */
    saveAllZones() {
      /**
       * Отдает объект `AnalyticZonesConfig` со всеми актуальными настройками по зонам.
       * @event save-zones
       */
      this.switchZonePainter();
      this.analyticZonesConfig.zones = this.zones;
      this.$emit("save-zones", this.analyticZonesConfig);
    },
    /**
     * Функция для получения объекта с настройками для vue компонента со скриншотом.
     * Он нужен для временной шкалы, чтобы над ней можно было бы получить показать скриншот для запрошенного времени.
     * Флюсоник для скриншотов требует дату именно как для UTC.
     *
     * @param {Date} screenshotDate
     * @return {Object}
     */
    getComponentWithScreenshot(screenshotDate) {
      if (this.availableArchiveFrom < screenshotDate && screenshotDate < this.availableArchiveTo) {
        // За пределами границ архива запрашивать скриншоты бессмысленно.
        // todo проверять попадание в доступный архив и не запрашивать скрины в противном случае.
        const screenshotUrl = `${this.httpProtocol}://${this.domain}/${this.cameraNumber}/${moment(screenshotDate).utc().format("YYYY/MM/DD/HH/mm/ss")}-preview.mp4?token=${this.archiveToken}`;
        return {
          functional: true,
          render(h) {
            return h("video", {class: "video-screenshot", attrs: {src: screenshotUrl, autoplay: "autoplay", muted: "muted"}});
          },
        };
      }
    },
    /**
     * Сделает скриншот video и скачает его.
     */
    takeScreenshot() {
      if (this.showError || this.isLoading) {
        return;
      }
      const videoWidth = this.$refs.videoPlayer.videoWidth,
            videoHeight = this.$refs.videoPlayer.videoHeight,
            canvasWithScreenshot = document.createElement("canvas");

      canvasWithScreenshot.width = videoWidth;
      canvasWithScreenshot.height = videoHeight;
      const canvasContext = canvasWithScreenshot.getContext("2d");
      canvasContext.fillRect(0, 0, videoWidth, videoHeight);
      canvasContext.drawImage(this.$refs.videoPlayer, 0, 0, videoWidth, videoHeight);

      downloadBase64Content(`${this.cameraNumber} ${this.$options.filters.localDateTime(this.timeShift)}.jpg`, canvasWithScreenshot.toDataURL("image/jpeg"));
    },
  },
};
</script>

<style lang="scss">
@use "./scss/style.scss";
</style>
